import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useDaggerheartData } from '../context/DataContext';
// Pattern-based implementation
import { ResourceHarvestPattern } from './campaign-frames/patterns';
import { getFrameMechanicConfig } from '../config/campaignFrameConfig';

/**
 * Feature flag for pattern-based harvesting.
 * Set to true to use the new config-driven ResourceHarvestPattern with tabs.
 * Set to false to use the legacy hardcoded implementation.
 */
const USE_PATTERN_HARVEST = true;

/**
 * HarvestIngredientsManager Component
 * Handles the ingredient harvesting action for Beast Feast campaigns.
 * Uses config-driven ResourceHarvestPattern with tabs for beast/bloom modes.
 *
 * @param {object} props - The component props.
 * @param {object} props.character - The current character data object.
 * @param {function} props.updateCharacter - Function to update the character state.
 * @param {function} props.onManagerClose - Callback to close the modal.
 */
const HarvestIngredientsManager = ({ character, updateCharacter, onManagerClose }) => {
  const allData = useDaggerheartData();
  const [harvestMode, setHarvestMode] = useState('beast'); // 'beast' or 'blooms'

  // Early return if data is not loaded
  if (!allData || !allData.campaignFrames || !allData.campaignFrames['beast-feast']) {
    return <div>Loading Beast Feast data...</div>;
  }

  // Use pattern-based implementation with tab switching
  if (USE_PATTERN_HARVEST) {
    const frameStaticData = allData.campaignFrames['beast-feast'];
    // Get BOTH configs from the extensible architecture
    const harvestConfig = getFrameMechanicConfig('beast-feast', 'harvest');
    const bloomsConfig = getFrameMechanicConfig('beast-feast', 'blooms');

    if (!harvestConfig || !bloomsConfig) {
      return <div>Loading harvest configuration...</div>;
    }

    // Select config based on current tab
    const activeConfig = harvestMode === 'beast' ? harvestConfig : bloomsConfig;
    const activeMechanicId = harvestMode === 'beast' ? 'harvest' : 'blooms';

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="text-center">
          <h3 className="text-xl font-bold text-green-600 mb-2">🐉🌸 Harvest & Gather</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Gather ingredients from defeated beasts or environmental blooms
          </p>
        </div>

        {/* Tab Switcher - Config-driven UI */}
        <div className="flex mb-4 bg-gray-800 rounded-lg p-1 border border-gray-600">
          <button
            onClick={() => setHarvestMode('beast')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
              harvestMode === 'beast'
                ? 'bg-green-800 text-green-200 border border-green-600'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            🐉 Beast Harvest
          </button>
          <button
            onClick={() => setHarvestMode('blooms')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
              harvestMode === 'blooms'
                ? 'bg-purple-800 text-purple-200 border border-purple-600'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            🌸 Gather Blooms
          </button>
        </div>

        {/* Pattern-based content - uses config from campaignFrameConfig.js */}
        <ResourceHarvestPattern
          frameId="beast-feast"
          mechanicId={activeMechanicId}
          config={activeConfig}
          character={character}
          updateCharacter={updateCharacter}
          frameStaticData={frameStaticData}
          onClose={onManagerClose}
        />

        {/* Close button */}
        <div className="flex justify-end pt-2">
          <button
            onClick={onManagerClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Legacy implementation below (when USE_PATTERN_HARVEST is false)

  // Beast HP dropdown options - every number from 1 to 200
  const beastHPOptions = Array.from({ length: 200 }, (_, i) => i + 1);

  const [beastHP, setBeastHP] = useState(0);
  const [harvestedIngredients, setHarvestedIngredients] = useState([]);
  const [isHarvesting, setIsHarvesting] = useState(false);
  const [harvestType, setHarvestType] = useState('beast'); // 'beast' or 'blooms'
  const [bloomsGatheredThisRest, setBloomsGatheredThisRest] = useState(0);
  const [ingredientSource, setIngredientSource] = useState('');
  const [isSpecialIngredient, setIsSpecialIngredient] = useState(false);
  const [specialFeatureName, setSpecialFeatureName] = useState('');
  const [specialFeatureDescription, setSpecialFeatureDescription] = useState('');

  const generateIngredientName = (isBloom = false) => {
    const mechanics = allData.campaignFrames['beast-feast'].customMechanics;
    const partTable = isBloom ? mechanics.ingredientTables.bloomTypes : mechanics.ingredientTables.beastParts;
    const traitTable = mechanics.ingredientTables.uniqueTraits;
    
    const part = partTable[Math.floor(Math.random() * partTable.length)];
    const trait = Math.random() < 0.3 ? traitTable[Math.floor(Math.random() * traitTable.length)] : null;
    
    const baseName = isBloom ? `${part}` : `Beast ${part}`;
    return trait ? `${baseName} (${trait})` : baseName;
  };

  const handleBeastHarvest = () => {
    try {
      setIsHarvesting(true);
      console.log('Harvest button clicked, beastHP:', beastHP);
      
      const beastFeastData = allData.campaignFrames['beast-feast'];
      if (!beastFeastData || !beastFeastData.customMechanics || 
          !beastFeastData.customMechanics.harvesting || !beastFeastData.customMechanics.cooking) {
        console.error('Beast Feast mechanics data not found');
        toast.success('Beast Feast mechanics data is not available. Please check the data files.');
        setIsHarvesting(false);
        return;
      }
      
      const { yields } = beastFeastData.customMechanics.harvesting;
      const { flavorProfiles } = beastFeastData.customMechanics.cooking;
      
      if (!yields || !flavorProfiles) {
        console.error('Missing yields or flavorProfiles in Beast Feast data');
        toast.success('Incomplete Beast Feast data. Please check the configuration.');
        setIsHarvesting(false);
        return;
      }
      
      const yieldRule = yields.find(y => beastHP <= y.maxHP) || yields[yields.length - 1];
      const numIngredients = yieldRule ? yieldRule.ingredients : 0;
      
      console.log('Yield rule:', yieldRule, 'Number of ingredients:', numIngredients);
     
      // Enhanced ingredient generation with random names and varied flavors
      const newIngredients = Array.from({ length: numIngredients }, (_, i) => {
          // More varied flavor generation - sometimes single, sometimes multiple flavors
          const numFlavors = Math.random() < 0.7 ? 1 : Math.random() < 0.8 ? 2 : 3;
          const selectedFlavors = [];
          
          for (let j = 0; j < numFlavors; j++) {
            const flavorProfile = flavorProfiles[Math.floor(Math.random() * flavorProfiles.length)];
            const strength = Math.floor(Math.random() * 3) + 1; // 1-3 strength
            
            // Avoid duplicate flavors in same ingredient
            if (!selectedFlavors.find(f => f.profile === flavorProfile.name)) {
              selectedFlavors.push({ profile: flavorProfile.name, strength });
            }
          }
          
          return {
              id: `harvest-${Date.now()}-${i}`,
              name: generateIngredientName(false),
              flavors: selectedFlavors,
              source: ingredientSource || 'Unknown Beast',
              ...(isSpecialIngredient && specialFeatureName && {
                isSpecial: true,
                specialFeature: {
                  name: specialFeatureName,
                  description: specialFeatureDescription
                }
              })
          };
      });
      
      console.log('Ingredient source when generating:', ingredientSource);
      console.log('Generated ingredients:', newIngredients);
      setHarvestedIngredients(newIngredients);
      setIsHarvesting(false);
      setIngredientSource(''); // Clear source field
      setIsSpecialIngredient(false); // Clear special ingredient checkbox
      setSpecialFeatureName(''); // Clear special feature name
      setSpecialFeatureDescription(''); // Clear special feature description
    } catch (error) {
      console.error('Error in handleHarvest:', error);
      toast.success(`Error during harvest: ${error.message}`);
      setIsHarvesting(false);
    }
  };

  const handleBloomGather = () => {
    if (character.hope < 1) {
      toast.success('You need at least 1 Hope to gather blooms!');
      return;
    }
    
    if (bloomsGatheredThisRest >= 1) {
      toast.success('You can only gather blooms once per rest!');
      return;
    }

    try {
      setIsHarvesting(true);
      
      const mechanics = allData.campaignFrames['beast-feast'].customMechanics;
      const { blooms } = mechanics;
      
      // Roll Hope die
      const hopeRoll = Math.ceil(Math.random() * 12);
      
      // Find matching bloom from table
      const bloomResult = blooms.rollTable.find(entry => 
        hopeRoll >= entry.range[0] && hopeRoll <= entry.range[1]
      );
      
      if (!bloomResult) {
        toast.success('Error rolling for bloom!');
        setIsHarvesting(false);
        return;
      }
      
      const newBloom = {
        id: `bloom-${Date.now()}`,
        name: generateIngredientName(true),
        flavors: [{ profile: bloomResult.profile, strength: bloomResult.strength }],
        source: ingredientSource || 'Unknown Location',
        hopeRoll: hopeRoll,
        ...(isSpecialIngredient && specialFeatureName && {
          isSpecial: true,
          specialFeature: {
            name: specialFeatureName,
            description: specialFeatureDescription
          }
        })
      };
      
      console.log('Bloom ingredient source when generating:', ingredientSource);
      console.log('Generated bloom:', newBloom);

      // Consume Hope and track blooms gathered
      updateCharacter({ ...character, hope: character.hope - 1 });
      setBloomsGatheredThisRest(prev => prev + 1);
      setHarvestedIngredients([newBloom]);
      setIsHarvesting(false);
      setIngredientSource(''); // Clear source field
      setIsSpecialIngredient(false); // Clear special ingredient checkbox
      setSpecialFeatureName(''); // Clear special feature name
      setSpecialFeatureDescription(''); // Clear special feature description
      
    } catch (error) {
      console.error('Error in handleBloomGather:', error);
      toast.success(`Error gathering blooms: ${error.message}`);
      setIsHarvesting(false);
    }
  };

  const handleRest = () => {
    setBloomsGatheredThisRest(0);
    toast.success('Rest completed! You can now gather blooms again.');
  };

  const handleAddToInventory = () => {
    const newInventory = [...(character.inventory?.ingredients || []), ...harvestedIngredients];
    updateCharacter({
      ...character,
      inventory: {
        ...character.inventory,
        ingredients: newInventory
      }
    });
    setHarvestedIngredients([]);
    setBeastHP(0);
  };

  const handleAddAndClose = () => {
    handleAddToInventory();
    onManagerClose();
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-green-600 mb-2">🐉🌸 Harvest & Gather</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Gather ingredients from defeated beasts or environmental blooms for future feast preparation
        </p>
      </div>

      {/* Harvest Type Selection */}
      <div className="flex mb-4 bg-gray-800 rounded-lg p-1 border border-gray-600">
        <button
          onClick={() => setHarvestType('beast')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
            harvestType === 'beast'
              ? 'bg-green-800 text-green-200 border border-green-600'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          Beast Harvest
        </button>
        <button
          onClick={() => setHarvestType('blooms')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
            harvestType === 'blooms'
              ? 'bg-purple-800 text-purple-200 border border-purple-600'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          Gather Blooms
        </button>
      </div>

      {harvestType === 'beast' ? (
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">🐉 Beast Harvesting</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Enter the maximum HP of a defeated beast to determine harvest yield
          </p>
          
          <div className="flex space-x-2 mb-3">
            <input
              type="text"
              value={ingredientSource}
              onChange={e => setIngredientSource(e.target.value)}
              className="flex-1 p-3 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Source (e.g., White Dragon, Dire Wolf)"
            />
          </div>
          
          {/* Special Ingredient Section */}
          <div className="mb-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <label className="flex items-center mb-3">
              <input
                type="checkbox"
                checked={isSpecialIngredient}
                onChange={e => setIsSpecialIngredient(e.target.checked)}
                className="h-4 w-4 rounded text-yellow-600 focus:ring-yellow-500 mr-2"
              />
              <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                ⭐ Special Ingredient (has unique features)
              </span>
            </label>
            
            {isSpecialIngredient && (
              <div className="space-y-2">
                <input
                  type="text"
                  value={specialFeatureName}
                  onChange={e => setSpecialFeatureName(e.target.value)}
                  className="w-full p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-yellow-500 text-sm"
                  placeholder="Feature name (e.g., Dragon's Blessing, Ancient Power)"
                />
                <textarea
                  value={specialFeatureDescription}
                  onChange={e => setSpecialFeatureDescription(e.target.value)}
                  className="w-full p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-yellow-500 text-sm"
                  placeholder="Feature description (e.g., Gain 1 Hope when used in cooking, +2 to meal rating)"
                  rows="2"
                />
              </div>
            )}
          </div>
          
          <div className="flex space-x-2">
            <select
              value={beastHP}
              onChange={e => setBeastHP(parseInt(e.target.value))}
              className="flex-1 p-2 mt-1 rounded-md bg-gray-700 border border-gray-600 text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value={0}>Select Beast's Max HP</option>
              {beastHPOptions.map(hp => (
                <option key={hp} value={hp}>{hp} HP</option>
              ))}
            </select>
            <button
              onClick={handleBeastHarvest}
              disabled={beastHP <= 0 || isHarvesting}
              className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              {isHarvesting ? 'Harvesting...' : 'Harvest'}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
          <h4 className="font-semibold mb-3 text-purple-800 dark:text-purple-200">🌸 Bloom Gathering</h4>
          <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
            Spend 1 Hope to gather environmental blooms. Roll your Hope die to determine what you find.
            <strong> Once per rest only.</strong>
          </p>
          
          <div className="flex justify-between items-center mb-3">
            <div className="text-sm">
              <span className="text-purple-600 dark:text-purple-400">Hope Cost: 1</span>
              <span className="ml-4 text-gray-600 dark:text-gray-400">
                Current Hope: {character.hope}/6
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-sm">
                <span className={bloomsGatheredThisRest >= 1 ? 'text-red-600' : 'text-green-600'}>
                  Blooms Gathered: {bloomsGatheredThisRest}/1
                </span>
              </div>
              <button
                onClick={handleRest}
                className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                title="Take a rest to reset bloom gathering"
              >
                Take Rest
              </button>
            </div>
          </div>
          
          <div className="mb-3">
            <input
              type="text"
              value={ingredientSource}
              onChange={e => setIngredientSource(e.target.value)}
              className="w-full p-3 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Location/Source (e.g., Moonlit Glade, Ancient Ruins)"
            />
          </div>
          
          {/* Special Ingredient Section */}
          <div className="mb-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <label className="flex items-center mb-3">
              <input
                type="checkbox"
                checked={isSpecialIngredient}
                onChange={e => setIsSpecialIngredient(e.target.checked)}
                className="h-4 w-4 rounded text-yellow-600 focus:ring-yellow-500 mr-2"
              />
              <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                ⭐ Special Ingredient (has unique features)
              </span>
            </label>
            
            {isSpecialIngredient && (
              <div className="space-y-2">
                <input
                  type="text"
                  value={specialFeatureName}
                  onChange={e => setSpecialFeatureName(e.target.value)}
                  className="w-full p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-yellow-500 text-sm"
                  placeholder="Feature name (e.g., Ancient Bloom Essence, Crystal Nectar)"
                />
                <textarea
                  value={specialFeatureDescription}
                  onChange={e => setSpecialFeatureDescription(e.target.value)}
                  className="w-full p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-yellow-500 text-sm"
                  placeholder="Feature description (e.g., Reroll 1s in meal dice, Clear a condition when consumed)"
                  rows="2"
                />
              </div>
            )}
          </div>
          
          <button 
            onClick={handleBloomGather} 
            disabled={character.hope < 1 || bloomsGatheredThisRest >= 1 || isHarvesting}
            className="w-full px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
          >
            {isHarvesting ? 'Gathering...' : 'Gather Blooms (Roll Hope Die)'}
          </button>
          
          {(character.hope < 1 || bloomsGatheredThisRest >= 1) && (
            <p className="text-sm text-red-600 mt-2">
              {character.hope < 1 ? 'Not enough Hope!' : 'Already gathered blooms this rest!'}
            </p>
          )}
        </div>
      )}

      {harvestedIngredients.length > 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
          <h4 className="font-semibold text-green-800 dark:text-green-200 mb-3">
            🎒 {harvestType === 'blooms' ? 'Gathered Blooms' : 'Harvested Ingredients'} ({harvestedIngredients.length})
          </h4>
          <div className="space-y-2 mb-4">
            {harvestedIngredients.map((ing) => (
              <div key={ing.id} className="flex items-center p-3 bg-white dark:bg-gray-700 rounded-md border">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{ing.name}</span>
                    {ing.isSpecial && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 px-2 py-1 rounded font-medium">
                        ⭐ Special
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Flavors: {ing.flavors.map(f => `${f.profile} (${f.strength})`).join(', ')}
                  </div>
                  {ing.source && (
                    <div className="text-xs text-blue-600 dark:text-blue-400">
                      Source: {ing.source}{ing.hopeRoll ? ` (Hope roll: ${ing.hopeRoll})` : ''}
                    </div>
                  )}
                  {ing.isSpecial && ing.specialFeature && (
                    <div className="text-xs text-yellow-700 dark:text-yellow-300 mt-1 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                      <strong>{ing.specialFeature.name}:</strong> {ing.specialFeature.description}
                    </div>
                  )}
                </div>
                <div className="text-lg">
                  {ing.hopeRoll ? '🌸' : '🦴'}
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex space-x-2">
            <button 
              onClick={handleAddToInventory} 
              className="flex-1 bg-green-600 text-white text-sm py-2 px-4 rounded-md hover:bg-green-700 font-medium"
            >
              Add to Inventory
            </button>
            <button 
              onClick={handleAddAndClose} 
              className="flex-1 bg-blue-600 text-white text-sm py-2 px-4 rounded-md hover:bg-blue-700 font-medium"
            >
              Add & Close
            </button>
          </div>
        </div>
      )}

      {/* Current Inventory Display */}
      {character.inventory?.ingredients && character.inventory.ingredients.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">Current Inventory ({character.inventory.ingredients.length} ingredients)</h4>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {character.inventory.ingredients.map(ing => (
              <div key={ing.id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-700 rounded-md text-sm">
                <span>{ing.name}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {ing.flavors?.[0]?.profile || 'Unknown'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button 
          onClick={onManagerClose}
          className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default HarvestIngredientsManager;
